import { type SocketState, socketStore } from '../../stores/index.ts';

/**
 * 소켓이 reconnecting 상태일 때 풀스크린 오버레이를 표시.
 * 입력을 차단하고 사용자에게 재연결 진행 상황을 알림.
 * disconnected/connected 상태에선 hidden.
 */
const SHOW_DELAY_MS = 1200;

export class ReconnectOverlay {
  private root: HTMLElement;
  private unsubscribe: (() => void) | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private visible = false;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className =
      'reconnect-overlay fixed inset-0 z-[70] hidden items-center justify-center bg-wood-coffee/75 backdrop-blur-sm';
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');
    parent.appendChild(this.root);
  }

  mount(): void {
    this.apply(socketStore.get());
    this.unsubscribe = socketStore.subscribe((next, prev) => {
      if (next.status === prev.status && next.reconnectAttempt === prev.reconnectAttempt) return;
      this.apply(next);
    });
  }

  unmount(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.clearShowTimer();
    this.root.remove();
  }

  private apply(state: SocketState): void {
    if (state.status !== 'reconnecting') {
      this.clearShowTimer();
      this.hide();
      return;
    }
    if (this.visible) {
      this.render(state);
      return;
    }
    if (this.showTimer !== null) return;
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      const cur = socketStore.get();
      if (cur.status !== 'reconnecting') return;
      this.show(cur);
    }, SHOW_DELAY_MS);
  }

  private clearShowTimer(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.add('hidden');
    this.root.classList.remove('flex');
    this.root.innerHTML = '';
  }

  private show(state: SocketState): void {
    this.visible = true;
    this.render(state);
  }

  private render(state: SocketState): void {
    this.root.classList.remove('hidden');
    this.root.classList.add('flex');
    const attempt = state.reconnectAttempt;
    const attemptLabel = attempt > 0 ? `${attempt}회 시도 중` : '연결 시도 중';
    this.root.innerHTML = `
      <div class="flex flex-col items-center gap-4 rounded-2xl bg-wood-paper ring-2 ring-card-gold/50 p-6 max-w-sm mx-4 shadow-xl">
        <div class="flex items-center gap-3">
          <span class="inline-block w-3 h-3 rounded-full bg-card-gold animate-pulse"></span>
          <span class="inline-block w-3 h-3 rounded-full bg-card-gold animate-pulse" style="animation-delay:0.2s"></span>
          <span class="inline-block w-3 h-3 rounded-full bg-card-gold animate-pulse" style="animation-delay:0.4s"></span>
        </div>
        <h2 class="text-title text-card-brass">재연결 중…</h2>
        <p class="text-body text-wood-coffee text-center">
          서버와 연결이 끊겼습니다. 자동으로 다시 연결을 시도합니다.
        </p>
        <p class="text-caption text-wood-coffee/65 tabular-nums">${attemptLabel}</p>
      </div>
    `;
  }
}
